/**
 * Leaderboard Screen - local asset ranking (player + NPC rankers).
 * Sub-screen of the exchange tab. NPC net worth grows over real time, so the
 * player's rank actually rises as their total assets (cash + holdings) grow.
 * Text-only, no emoji. Profit-red / loss-blue convention is not used here;
 * the player's own row is simply highlighted.
 */
import { formatNumber } from '../game/utils.js';
import { t, tv } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import {
  buildLadder, getPlayerRank, getPlayerAssets, ladderSize, type LadderEntry,
} from '../game/systems/leaderboardSystem.js';
import { onRender, getState, setViewState, setInnerHTML } from './renderer.js';

let container: HTMLDivElement | null = null;

export function initLeaderboardScreen() {
  const content = document.getElementById('screen-content');
  if (!content) return;
  container = document.createElement('div');
  container.id = 'leaderboard-screen';
  container.className = 'screen-panel exchange-theme';
  content.appendChild(container);
  onRender(renderLeaderboard, { alwaysTabs: ['leaderboard'] });
}

/** Highlight the top-3 with rank medals-as-text (no emoji): #1 / #2 / #3. */
function rankBadge(rank: number): string {
  const cls = rank <= 3 ? ` lb-rank-top lb-rank-${rank}` : '';
  return `<span class="lb-rank${cls}">#${rank}</span>`;
}

function rowHtml(entry: LadderEntry, rank: number): string {
  const name = entry.isPlayer ? t('lb_you') : entry.name;
  const cls = entry.isPlayer ? ' lb-row-player' : '';
  return `<div class="lb-row${cls}">
    ${rankBadge(rank)}
    <span class="lb-name">${name}</span>
    <span class="lb-assets">${formatNumber(entry.assets)}</span>
  </div>`;
}

function renderLeaderboard() {
  if (!container) return;
  const st = getState();
  const now = Date.now();
  const ladder = buildLadder(st, now);
  const rank = getPlayerRank(st, now);
  const total = ladderSize();
  const best = st.leaderboard?.bestRank || rank;

  let html = `<button class="ex-back-btn" data-action="back">&lt; ${t('ex_back')}</button>`;
  html += `<div class="ex-header">${t('lb_title')}</div>`;
  html += `<div class="lb-summary">
    <div class="lb-summary-main">${tv('lb_your_rank', { rank: String(rank), total: String(total) })}</div>
    <div class="lb-summary-sub">
      <span>${t('lb_your_assets')}: ${formatNumber(getPlayerAssets(st))}</span>
      <span>${tv('lb_best_rank', { rank: String(best) })}</span>
    </div>
  </div>`;

  html += '<div class="lb-list">';
  ladder.forEach((entry, i) => { html += rowHtml(entry, i + 1); });
  html += '</div>';
  html += `<div class="lb-footnote">${t('lb_footnote')}</div>`;

  if (setInnerHTML(container, html)) {
    container.querySelector('[data-action="back"]')?.addEventListener('click', () => {
      playSfx('tap');
      setViewState({ currentTab: 'exchange' });
    });
  }
}
